import React from 'react';
import { useTranslation } from 'react-i18next';
import { Close } from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import { Button, Dialog, IconButton, Typography } from '@mui/material';

import LinkableDevicesTable from 'features/device/linkable-device/views/linkable-devices-table';
import OrganisationUnitDevice from 'features/organisation/domain/models/organisation-unit-device';
import useOrganisationDetailsLinkableDevicesModal from 'features/organisation/views/organisation-details/components/organisation-details-linkable-devices-modal/hooks/use-organisation-details-linkable-devices-modal';

import './organisation-details-linkable-devices-modal.component.scss';

import { useDeviceLinkContextProvider } from 'features/device/providers/device-link-provider/device-link-provider';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onLinkButtonClicked: (selectedDevices: OrganisationUnitDevice[]) => void;
};

const OrganisationDetailsLinkableDevicesModal = ({ isOpen, onClose, onLinkButtonClicked }: Props): React.JSX.Element => {
  const { t } = useTranslation('organisation');
  const { selectedDevices } = useDeviceLinkContextProvider();

  const { hasDeviceLinkError, handleOnClose, handleOnLinkButtonClicked, getErrorMessageForDevice } =
    useOrganisationDetailsLinkableDevicesModal({
      onClose,
      onLinkButtonClicked,
    });

  return (
    <Dialog
      className="organisation-details-linkable-devices-modal"
      open={isOpen}
      onClose={handleOnClose}
      slotProps={{
        paper: {
          className: 'linkable-devices-modal-paper',
          sx: { overflow: 'visible' },
        },
      }}
    >
      <div className="linkable-devices-modal-container">
        <div className="linkable-devices-modal-header-container">
          <Typography className="header-title" variant="h3">
            {t('linkableDevicesModal.title')}
          </Typography>
          <div className="linkable-devices-modal-header-actions-container">
            {selectedDevices.length < 1 && (
              <Button variant="contained" disabled={true}>
                {t('linkableDevicesModal.disabledLinkButtonText')}
              </Button>
            )}
            {selectedDevices.length > 0 && (
              <Button
                className="link-button"
                variant="contained"
                onClick={handleOnLinkButtonClicked}
                startIcon={<CheckIcon className="check-icon" />}
                disabled={hasDeviceLinkError !== undefined}
                data-testid="linkSelectedDevicesButton"
              >
                {t('linkableDevicesModal.enabledLinkButtonText', {
                  amountOfSelectedDevices: selectedDevices.length,
                })}
              </Button>
            )}
            <IconButton aria-label="close" className="close-button" onClick={handleOnClose}>
              <Close />
            </IconButton>
          </div>
        </div>
        <LinkableDevicesTable getErrorMessageForDevice={getErrorMessageForDevice} />
      </div>
    </Dialog>
  );
};

export default OrganisationDetailsLinkableDevicesModal;
